import { useMemo, type VoidFunctionComponent } from 'react';

import { FULL_SECURITY, REPORTS_V3_NEW_UX_FLOW } from '../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { isFreeTier } from '../../../../shared/subscriptions';
import { useParams } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { useIsFeatureEnabled, useIsSecurityEnabled } from '../../../helpers/features';
import { Action, ResourceType } from '../../../shared/permissions';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import Subnav from '../../generic/Subnav';
import { vulnManagementFeature } from '../../settings/billing/features/consts';
import { useCanDisplaySecurityOptions } from '../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../generic/ReportGenerationV2/utils';
import type { ReportCategory } from '../generic/shared/types';
import type { RevisionRouteParams } from '../projectDashboardTypes';

type EnabledReports = Record<string, {
    title: string;
    isPremiumOnly: boolean;
  }>;

/**
 * Some reports might be completely restricted to only premium orgs. This Set tracks those.
 */
const premiumOnlyReports = new Set<ReportCategory>([
  ReportCategories.RemediationGuidance,
]);

/**
 * The Reports V3 routes use camelCase slugs (eg. "remediationGuidance") while the old
 * ReportCategories use kebab-case (eg. "remediation-guidance"). This map bridges
 * the mismatch for any category whose slug differs between the two systems.
 *
 * TODO: Once REPORTS_V3_NEW_UX_FLOW is GA and the flag is removed, update ReportCategories
 * to use the camelCase values directly and remove this map.
 */
const reportsV3SlugOverrides: Partial<Record<ReportCategory, string>> = {
  [ReportCategories.RemediationGuidance]: 'remediationGuidance',
};

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag and the project fetcher type.
 */
const useEnabledReportTypes = (): EnabledReports => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSecurityEnabled = useIsSecurityEnabled();

  const params = useParams<RevisionRouteParams>();
  const { fetcher } = Locator.parse(params.project_id);
  const isBinaryProject = fetcher === BinaryFetcherName;

  return useMemo(() => {
    const excludedReports = new Set();
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.add(ReportCategories.Vulnerability);
    }
    if (!isSecurityEnabled || isBinaryProject) {
      excludedReports.add(ReportCategories.RemediationGuidance);
    }

    return Object.entries(ReportCategoryTitles).reduce<EnabledReports>((acc, [reportType, title]) => {
      if (excludedReports.has(reportType)) {
        return acc;
      }

      acc[reportType] = {
        title,
        isPremiumOnly: premiumOnlyReports.has(reportType as ReportCategory),
      };
      return acc;
    }, {});
  }, [isSecurityAndPDFReportsEnabled, isSecurityEnabled, isBinaryProject]);
};

type Props = {
  baseRevisionURL: string;
};

/**
 * ProjectSubnavItemReports is the dropdown subnav item for project Reports.
 */
const ProjectSubnavItemReports: VoidFunctionComponent<Props> = ({ baseRevisionURL }) => {
  const params = useParams<RevisionRouteParams>();
  const hasPermission = useHasPermission();

  const isNewReportsFlowEnabled = useIsFeatureEnabled(REPORTS_V3_NEW_UX_FLOW);
  //  In the new reports flow, the settings live under the project settings, not in reports.
  const shouldShowSettings = !isNewReportsFlowEnabled && !isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });

  const enabledReportTypes = useEnabledReportTypes();
  const isFullSecurityEnabled = useIsFeatureEnabled(FULL_SECURITY);

  const canGenerateReport = hasPermission({ resourceType: ResourceType.ProjectReportAny, action: Action.Create })
    || hasPermission({ resourceType: ResourceType.ProjectReport, action: Action.Create, projectId: params.project_id });

  if (!canGenerateReport) {
    return null;
  }

  return (
    <Subnav.Dropdown to={`${baseRevisionURL}/reports`} navKey="reports">
      <Subnav.Dropdown.RootItem>Reports</Subnav.Dropdown.RootItem>
      {Object.entries(enabledReportTypes).map(([reportType, data]) => {
        const slug = isNewReportsFlowEnabled
          ? (reportsV3SlugOverrides[reportType as ReportCategory] ?? reportType)
          : reportType;

        if (data.isPremiumOnly) {
          return (
            <Subnav.Dropdown.PaidFeatureItem
              key={`reports/${slug}`}
              to={`${baseRevisionURL}/reports/${slug}`}
              navKey={`reports/${slug}`}
              modalFocusFeature={vulnManagementFeature}
              isRestricted={!isFullSecurityEnabled}
            >
              {data.title}
            </Subnav.Dropdown.PaidFeatureItem>
          );
        }

        return (
          <Subnav.Dropdown.Item
            key={`reports/${slug}`}
            to={`${baseRevisionURL}/reports/${slug}`}
            navKey={`reports/${slug}`}
          >
            {data.title}
          </Subnav.Dropdown.Item>
        );
      },
      )}
      {shouldShowSettings && (
        <Subnav.Dropdown.Item
          key="reports/settings"
          to={`${baseRevisionURL}/reports/settings`}
          navKey="reports/settings"
        >
          Settings
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ProjectSubnavItemReports;
